import {
  slackIdentityStartSchema,
  slackOAuthStartResponseSchema,
} from "@io-spoke/contracts/integrations";
import {
  retrySlackConversationRequestSchema,
  sendSlackConversationRequestSchema,
  slackConversationDeliveryResponseSchema,
  slackConversationStatusResponseSchema,
} from "@io-spoke/contracts/slackConversations";
import { getApiClient } from "./apiClient";

function slackPath(caseId: string, conversationId: string) {
  return `/rescues/${encodeURIComponent(caseId)}/conversation/${encodeURIComponent(conversationId)}/slack`;
}

export async function getSlackConversationStatus(
  caseId: string,
  conversationId: string,
) {
  const response = await getApiClient().get(slackPath(caseId, conversationId));
  const parsed = slackConversationStatusResponseSchema.safeParse(response.data);
  if (!parsed.success)
    throw new Error(
      "Slack delivery status is unavailable. Reload and try again.",
    );
  return parsed.data;
}

export async function sendSlackConversation(
  caseId: string,
  conversationId: string,
  connectionId: string,
) {
  const payload = sendSlackConversationRequestSchema.parse({ connectionId });
  const response = await getApiClient().post(
    slackPath(caseId, conversationId),
    payload,
  );
  const parsed = slackConversationDeliveryResponseSchema.safeParse(
    response.data,
  );
  if (!parsed.success)
    throw new Error(
      "Slack delivery could not be confirmed. Reload its status before trying again.",
    );
  return parsed.data.delivery;
}

export async function retrySlackConversation(
  caseId: string,
  conversationId: string,
  deliveryId: string,
) {
  const payload = retrySlackConversationRequestSchema.parse({});
  const response = await getApiClient().post(
    `${slackPath(caseId, conversationId)}/${encodeURIComponent(deliveryId)}/retry`,
    payload,
  );
  const parsed = slackConversationDeliveryResponseSchema.safeParse(
    response.data,
  );
  if (!parsed.success)
    throw new Error(
      "Slack retry could not be confirmed. Reload its status before trying again.",
    );
  return parsed.data.delivery;
}

export async function startSlackConversationIdentity(
  connectionId: string,
  rescueCaseId?: string,
) {
  const payload = slackIdentityStartSchema.parse({
    connectionId,
    rescueCaseId,
  });
  const response = await getApiClient().post(
    "/integrations/slack/identity/start",
    payload,
  );
  const parsed = slackOAuthStartResponseSchema.safeParse(response.data);
  if (!parsed.success)
    throw new Error("Slack sign-in could not be started. Try again.");
  const url = new URL(parsed.data.authorizationUrl);
  if (
    url.protocol !== "https:" ||
    url.hostname !== "slack.com" ||
    url.username ||
    url.password
  ) {
    throw new Error("Slack returned an invalid sign-in destination.");
  }
  return url.toString();
}
